import { format, formatDistanceToNow } from "date-fns";

/**
 * Humanized relative time WITHOUT the "ago" suffix — "less than a minute", "5 minutes",
 * "about 1 hour", "2 days". Uses the non-strict distance so it BUCKETS instead of counting exact
 * seconds (avoids the "5s → 10s" flicker on re-render). Empty string if unknown. Callers append
 * " ago" where they need it (e.g. "Requested 5 minutes ago").
 */
export const formatRelativeShort = (iso: string | null): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return formatDistanceToNow(date);
};

/**
 * Humanized relative time WITH the "ago" suffix — "less than a minute ago", "about 1 hour ago",
 * "2 days ago". Non-strict (buckets, no per-second flicker). Falls back to "recently".
 *
 * @deprecated No longer used since the pending panels moved to absolute dates via {@link formatLongDate}.
 */
export const formatRelativeLong = (iso: string | null): string => {
  if (!iso) return "recently";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "recently";
  return formatDistanceToNow(date, { addSuffix: true });
};

/** Absolute long date — "3 August 2026". Empty string if unknown (callers omit the "on {date}"). */
export const formatLongDate = (iso: string | null): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return format(date, "d MMMM yyyy");
};

/** Two-letter initials from a display name (falls back to the first two chars). */
export const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};
